import { createSelector } from '@reduxjs/toolkit';
import { selectCurrentDesk } from 'store/desksSlice/desksSelectors';
import { selectIsAdminOrTeamLeader } from 'store/authSlice/authSelectors';

export const selectFeatureFlagSlice = (state) => state.featureFlag;

export const selectFeatureFlagData = createSelector([selectFeatureFlagSlice], (slice) => slice?.featureFlagData);

export const ACTIVITY_EVENTS_FLAG_KEY = 'activityEventsEnabled';

export const selectIsActivityEventsEnabled = createSelector([selectFeatureFlagData], (data) => !!data?.[ACTIVITY_EVENTS_FLAG_KEY]);

/**
 * The effective activity-tracking gate for the current desk (BEAST-13284): the global flag AND the
 * per-desk `activityEventsEnabled` on the desk the agent is currently working on. Both must be true
 * for events to be produced. `currentDesk` is `{}` when the agent is on no desk, which reads false.
 *
 * Note: `activityEventsEnabled` here is the DESK object's field (from `GET /desks`), a different
 * thing from the identically named global feature-flag key above.
 */
export const selectIsActivityEventsEnabledForCurrentDesk = createSelector(
    [selectIsActivityEventsEnabled, selectCurrentDesk],
    (globalEnabled, currentDesk) => globalEnabled && !!currentDesk?.activityEventsEnabled
);

// BEAST-13647: desks, locations, gates and cameras for which NACK is disabled on Kurento streams. Backend exposes the
// flag's config as `<code>Config`: { deskIds: { "<deskId>": {...} }, exclude: {...} }, see utils/kurentoNack.js.
// Undefined while the flag is inactive.
export const KURENTO_NACK_CONFIG_FLAG_KEY = 'kurentoNackConfigEnabled';

export const selectKurentoNackConfig = createSelector([selectFeatureFlagData], (data) =>
    data?.[KURENTO_NACK_CONFIG_FLAG_KEY] ? data?.[`${KURENTO_NACK_CONFIG_FLAG_KEY}Config`] : undefined
);

// Kill switch for transaction substreams: the backend decides which cameras get SUB (see utils/transactionSubStream.js),
// the frontend only plays them while the flag is active.
export const TRANSACTION_SUB_STREAM_CONFIG_FLAG_KEY = 'transactionSubStreamConfigEnabled';

export const selectIsTransactionSubStreamEnabled = createSelector(
    [selectFeatureFlagData],
    (data) => !!data?.[TRANSACTION_SUB_STREAM_CONFIG_FLAG_KEY]
);

export const selectCanManageDeskActivityTracking = createSelector(
    [selectIsActivityEventsEnabled, selectIsAdminOrTeamLeader],
    (globalEnabled, isAdminOrTeamLeader) => globalEnabled && isAdminOrTeamLeader
);
